import * as React from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useLocation, useNavigate } from "react-router";
import HttpLeadRepository from "../../../../api/request/Lead/Lead.service";
import HttpPaymentRepository from "../../../../api/request/Payment/Payment.service";
import { GetlifeContext } from "../../../../context/GetLifeContext.context";
import { I18nContext } from "../../../../context/i18n.context";
import { StripeIntentStatusI } from "../dashboard/Policy/ModalPolicy/ModalPolicy.controller";
import { useDevice } from "get-life-storybook-ts";
import { DOMAIN } from "../../../../constants/Global";
import { activeCapitalsFrom, hasActiveCapitals } from "../../../../utils/activeCapitals";
import { withAnnualFeePromoted } from "../../../../utils/pricesV3";
import { NewCheckoutI, PurchasePeriodT } from "../../../../api/request/Lead/Interfaces/DataInterface.response";
import HttpCouponsRepository from "../../../../api/request/Coupons/Coupons.service";
import { NativeCouponViewI } from "../../../../api/request/Coupons/Response/NativeCoupon.response";
import { GetLifeTrackerContext } from "../../../../context/GetLifeTracker.context";
import { track, trackSubmissionError } from "../../../../helpers/Tracking";
import { isMockPayment, paymentMockCheckout, paymentMockSetupIntent } from "./__fixtures__/paymentMockData";
import { isNativeMode } from "../../../../native/nativeMode";
import { visibleCoupon } from "../../../../native/nativeCoupon";

declare global {
  interface Window {
    gtag?: any;
    ttq?: any;
  }
}

export type ValidateCouponT = "validate" | "invalidate" | "error" | "validate-null";

export interface CouponInfoI {
  conditions?: string;
  exceptions?: string;
  error?: string;
  code?: string;
}

/**
 * Cupón del lead native (ms-agl vía gateway). `view` null = cargando o cupones
 * apagados: sin casilla de cupón. `error` = código del gateway/ms-agl.
 */
export interface NativeCouponStateI {
  view: NativeCouponViewI | null;
  error?: string;
  busy: boolean;
  apply: (code: string) => Promise<boolean>;
  remove: () => Promise<boolean>;
}

/** Markets whose payment page loads prices + capitals from /api/pricing/capitals. */
const PRICING_CAPITALS_MARKETS = ["sql_es", "es", "wl_es"];
const LOADS_PRICING_CAPITALS = PRICING_CAPITALS_MARKETS.includes(DOMAIN ?? "");
/**
 * Markets that load BOTH frequencies' prices (real + promoted) from
 * /api/pricing/capitals, so the freq cards never depend on the selected period.
 * Without it the monthly card fell back to `checkout.priceToPay`, which is the
 * price of the SELECTED period (IT showed the yearly price inside "Mensile").
 * IT keeps its capitals from /api/lead/payment (see getCheckoutData) and only
 * takes prices + promotion from this endpoint.
 */
const PRICES_MARKETS = [...PRICING_CAPITALS_MARKETS, "it"];
const LOADS_PRICES = PRICES_MARKETS.includes(DOMAIN ?? "");
/** Markets whose annual premium is not 12 × monthly: see withAnnualFeePromoted. */
const DERIVES_ANNUAL_FEE = DOMAIN === "it";

const PaymentPageController = () => {
  const { setBackFlag, leadId, token, setLoading, loading, handleCallMe, domain } =
  React.useContext(GetlifeContext);

  const navigate = useNavigate();
  const location = useLocation();
  const paymentLocation = location?.pathname.split("/")[1];
  const { isMobile } = useDevice();
  const [payConfirm, setPayConfirm] = React.useState(false);

  const [payConfirm2, setPayConfirm2] = React.useState(false);

  const [isUserDataAvailable, setIsUserDataAvailable] = React.useState(false);
  const [buttonContent, setButtonContent] = React.useState<any>("Hacer pago");

  const [openBox, setOpenBox] = React.useState({
    userData: false,
    creditCardData: false,
  });

  const [checkout, setCheckout] = React.useState<NewCheckoutI>();

  const [couponInfo, setCouponInfo] = React.useState<CouponInfoI>();

  const [purchasePeriod, setPurchasePeriod] =
    React.useState<PurchasePeriodT>((domain === "es" || domain === "wl_es") ? "yearly" : "monthly");

  const [validateCoupon, setValidateCoupon] = React.useState<ValidateCouponT>();

  const [nativeCouponView, setNativeCouponView] =
    React.useState<NativeCouponViewI | null>(null);
  const [nativeCouponError, setNativeCouponError] = React.useState<string>();
  const [nativeCouponBusy, setNativeCouponBusy] = React.useState(false);

  const [setupIntent, setSetupIntent] = React.useState<any>([]);

  const nodeConditionsModal: any = React.useRef(null);

  const handleConditions = () => nodeConditionsModal.current?.handleShowPortal();

  const [capitals, setCapitals] = React.useState({});
  const [prices, setPrices] = React.useState({});
  const [promotion, setPromotion] = React.useState({});

  const [stripeInputStatus, setStripeInputStatus] =
    React.useState<StripeIntentStatusI>({
      complete: false,
      error: undefined,
    });

  const {
    state: { translate },
  } = React.useContext(I18nContext);

  const { handleTrackerQuestion } = React.useContext(GetLifeTrackerContext);

  const leadRepository = new HttpLeadRepository(leadId, token);
  const paymentRepository = new HttpPaymentRepository(leadId, token);
  const couponsRepository = new HttpCouponsRepository(leadId, token);

  const handleIsUserDataAvailable = () => {
    setIsUserDataAvailable(true);
  };

  const handleOpenBox = (index: number | string, value: any) => {
    const prevValue = openBox;
    setOpenBox({ ...prevValue, [index]: value });
  };

  const goToUpsell = () => {
    setBackFlag(true);
    navigate(`/${paymentLocation}/1`);
  };

  const closeNameForm = () => handleOpenBox("userData", false);

  // TODO:
  const handleStripeStatus = (
    index: any,
    value: any,
    index2: any,
    value2: any
  ) => {
    const prevValue = stripeInputStatus;

    setStripeInputStatus({
      ...prevValue,
      [index]: value,
      [index2]: value2,
    });
  };

  // TODO:
  const handleIsLoading = (value: any) => {
    if (value === undefined) {
      setLoading(!loading);
    } else {
      setLoading(value);
    }
  };

  const handleCheckbox = () => setPayConfirm(!payConfirm);

  const handleCheckbox2 = () => setPayConfirm2(!payConfirm2);

  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLIC_KEY || "",
    {
      locale: "es",
      stripeAccount: process.env.REACT_APP_STRIPE_ACCOUNT,
    }
  );

  const isButtonDisabled = () => {
    if (DOMAIN === "it") {
      if (
        stripeInputStatus.complete === true &&
        stripeInputStatus.error === undefined &&
        payConfirm === true && payConfirm2 === true
      ) {
        return false;
      }
      return true;
    }

    if (
      stripeInputStatus.complete === true &&
      stripeInputStatus.error === undefined &&
      payConfirm === true
    ) {
      return false;
    }
    return true;

  };

  const navigateTo = () => {
    navigate("/confirmation");
  };

  const getCheckoutData = async () => {
    await leadRepository.getPaymentData().then((response: NewCheckoutI) => {
      setCheckout(response);
      // Native: los precios de AMBAS periodicidades vienen en la propia
      // paymentData (el monolito /pricing-capitals de getInfoSQL no aplica),
      // para que las tarjetas de frecuencia muestren cada una la suya.
      if (isNativeMode() && (response as any).prices) {
        setPrices((response as any).prices);
      }
      // Markets that do not call /api/pricing/capitals (IT, FR) take the
      // active capitals from the payment payload; without this the hero
      // fell back to `checkout.capital`, which the backend removed → "NaN €".
      if (!LOADS_PRICING_CAPITALS && hasActiveCapitals(response.capitals)) {
        setCapitals(activeCapitalsFrom(response.capitals));
      }
      setCouponInfo(
        response.promoConditions
          ? {
            conditions: response.promoConditions,
            exceptions: "",
            code: response.promotionalCode,
          }
          : undefined
      );
      if (response.isPromoted) {
        setValidateCoupon("validate")
      }
    });
  }

  const getInfoSQL = async () => {
    // Llamada a pricing-capitals
    await leadRepository.getPricingCapitals().then((response: any) => {
      if (LOADS_PRICING_CAPITALS) setCapitals(response.capitals);
      setPrices(
        DERIVES_ANNUAL_FEE
          ? withAnnualFeePromoted(response.prices) ?? {}
          : response.prices
      );
      setPromotion(response.promotion);
    });

  }

  React.useEffect(() => {
    if (isMockPayment()) {
      setCheckout(paymentMockCheckout);
      setSetupIntent(paymentMockSetupIntent);
      setIsUserDataAvailable(true);
      return;
    }
    (async () => {
      // Native: getInfoSQL va al monolito (/pricing-capitals), neutralizado; los
      // precios los trae getCheckoutData de la paymentData native.
      if (LOADS_PRICES && !isNativeMode()) {
        getInfoSQL()
      }
      await getCheckoutData();
    })()
  }, [validateCoupon, purchasePeriod]);

  React.useEffect(() => {
    if (!isNativeMode() || isMockPayment()) return;
    // Cupones apagados (503) o gateway caído: sin casilla, el pago sigue.
    couponsRepository
      .nativeCoupon()
      .then((view) => setNativeCouponView(visibleCoupon(view)))
      .catch(() => setNativeCouponView(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    isButtonDisabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeInputStatus]);

  React.useEffect(() => {
    if (checkout) {
      // En native el lead organic nace SIN nombre (se rellena en este form de
      // datos personales → agl_insured); la paymentData native devuelve "" (no
      // null), así que hay que tratar el vacío como "falta nombre" y abrir el
      // form antes del setup (el setup exige nombre). En legacy se mantiene === null.
      const missingUserData = isNativeMode()
        ? !checkout?.name || !checkout?.lastName
        : checkout?.name === null || checkout?.lastName === null;
      if (missingUserData) {
        handleOpenBox("userData", true);
      } else {
        setIsUserDataAvailable(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout]);

  React.useEffect(() => {
    if (isMockPayment()) return;
    if (isUserDataAvailable) {
      setLoading(true);
      if (checkout?.period) {
        handlePurchasePeriod(checkout?.period.toLowerCase());
      }
      paymentRepository.setup().then((res) => {
        const data = res;
        setSetupIntent(data);
        setLoading(false);
      });
      setOpenBox({ userData: false, creditCardData: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataAvailable]);

  React.useEffect(() => {
    if (!loading) {
      setButtonContent("Reintentar");
    }
  }, [loading]);

  const saveData = (data: any) => {
    leadRepository.saveData(data).then((res) => {
      getCheckoutData();
      handleIsUserDataAvailable();
    });
  };

  const handlePurchasePeriod = async (value: any) => {
    await leadRepository.updatePeriod(value);
    setPurchasePeriod(value);
  };

  const handleRemoveCoupon = async (code: string) => {
    try {
      await couponsRepository.removeCoupon({
        promotionalCode: code,
      });
      setValidateCoupon(undefined);
      setCouponInfo(undefined);
    } catch (error) { }
  };

  const fetchCheckPromo = async (
    code: string,
    yearlyPrice: number,
    func?: (code: string) => void
  ) => {
    const response = await couponsRepository.checkPromotionalCode({
      promotionalCode: code,
      yearlyPrice: yearlyPrice,
    });
    if (response.is_applicable_promo) {
      func && (await func(code));
      setValidateCoupon("validate");
      setCouponInfo({
        conditions: response.promo_conditions,
        exceptions: response.promo_exceptions,
        code: response.promo,
      });
    } else {
      track("promo_rejected", {});
      setValidateCoupon("invalidate");
    }
  };

  const addedCoupon = async (code: string) => {
    try {
      await couponsRepository.applyCoupon({ promotionalCode: code });
      track("promo_applied", { code });
      handleTrackerQuestion({
        type: "FS",
        event: `appliedCoupon`,
        data: {
          promotionalCode: code,
          leadId: leadId,
          market: domain
        },
      });
    } catch (error) {
      trackSubmissionError(error);
      setValidateCoupon("invalidate");
     }
  };

  const handleAddCoupon = async (code: string, yearlyPrice: number) => {
    try {
      await fetchCheckPromo(code, yearlyPrice, addedCoupon);
    } catch (error) {
      trackSubmissionError(error);
    }
  };

  // Native: el gateway aplica/quita el cupón en ms-agl y re-materializa el fee
  // con el % del año 1 nuevo; aquí se recarga la paymentData para el precio.
  const changeNativeCoupon = async (
    change: () => Promise<NativeCouponViewI>
  ): Promise<boolean> => {
    setNativeCouponBusy(true);
    setNativeCouponError(undefined);
    try {
      setNativeCouponView(await change());
    } catch (error: any) {
      setNativeCouponError(error?.message || "UNKNOWN");
      setNativeCouponBusy(false);
      return false;
    }
    await getCheckoutData().catch(() => undefined);
    setNativeCouponBusy(false);
    return true;
  };

  const applyNativeCoupon = async (code: string) => {
    const applied = await changeNativeCoupon(() =>
      couponsRepository.applyNativeCoupon(code)
    );
    if (applied) {
      track("promo_applied", { code });
      handleTrackerQuestion({
        type: "FS",
        event: `appliedCoupon`,
        data: { promotionalCode: code, leadId: leadId, market: domain },
      });
    } else {
      // ms-agl lo rechaza (COUPON_NOT_FOUND, COUPON_EXPIRED…).
      track("promo_rejected", {});
    }
    return applied;
  };

  const removeNativeCoupon = () =>
    changeNativeCoupon(() => couponsRepository.removeNativeCoupon());

  const nativeCoupon: NativeCouponStateI | undefined = isNativeMode()
    ? {
        view: nativeCouponView,
        error: nativeCouponError,
        busy: nativeCouponBusy,
        apply: applyNativeCoupon,
        remove: removeNativeCoupon,
      }
    : undefined;

  return {
    buttonContent,
    closeNameForm,
    goToUpsell,
    handleCheckbox,
    handleCheckbox2,
    handleIsLoading,
    handleOpenBox,
    handleStripeStatus,
    isButtonDisabled,
    isMobile,
    loading,
    isUserDataAvailable,
    leadId,
    navigateTo,
    openBox,
    payConfirm,
    payConfirm2,
    saveData,
    handleCallMe,
    stripePromise,
    stripeInputStatus,
    token,
    translate,
    checkout,
    nodeConditionsModal,
    handleConditions,
    couponInfo,
    validateCoupon,
    handlePurchasePeriod,
    handleRemoveCoupon,
    setupIntent,
    handleAddCoupon,
    nativeCoupon,
    capitals,
    prices,
    promotion
  };
};

export default PaymentPageController;
